import { normalizeWidgetTheme, APIURL, cancelSelectEvent, getCSSStyles, getWidgetDataURI, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseCryptoBreakDownByCountry {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private widgetIndex: number = 0;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading'); 
    this.attachStyles();
    await this.setupChart();
    if (widget) widget.classList.remove('loading'); 
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} Breakdown by Country</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(): Promise<void> {
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    try {
      this.currentData = {
        data: [
          {
              country: "United States",
              flag: "🇺🇸",
              populationPercentage: 30.4,
              cryptoOwners: 3,
              totalWorth: 75003493932.0
          },
          {
              country: "Japan",
              flag: "🇯🇵",
              populationPercentage: 21.2,
              cryptoOwners: 21,
              totalWorth: 6159703828.8
          },
          {
              country: "Canada",
              flag: "🇨🇦",
              populationPercentage: 15.6,
              cryptoOwners: 53,
              totalWorth: 4979703828.8
          },
          {
              country: "China",
              flag: "🇨🇳",
              populationPercentage: 13.5,
              cryptoOwners: 12,
              totalWorth: 4543703828.0
          },
          {
              country: "India",
              flag: "🇮🇳",
              populationPercentage: 13.4,
              cryptoOwners: 665,
              totalWorth: 3979703828.8
          },
          {
              country: "United Arab Emirates",
              flag: "🇦🇪",
              populationPercentage: 12.0,
              cryptoOwners: 26,
              totalWorth: 2875703828.3
          },
          {
              country: "Australia",
              flag: "🇦🇺",
              populationPercentage: 11.4,
              cryptoOwners: 4,
              totalWorth: 1979703828.5
          }
        ]
      };
      
      if (!this.currentData?.data || this.currentData.data.length === 0) {
        widgetBox?.removeAttribute('data-testing-id');
      }
      
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error setting up country data:", err);
      widgetBox?.removeAttribute('data-testing-id');
    }
  }

  private displayCountryData(): void {
    if (!this.currentData?.data) return;
    let filtered = this.currentData.data;
    this.renderCountryData(filtered);
  }

  private renderCountryData(countryData: any[]): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (!container) return;

    if (!countryData || countryData.length === 0) {
      widgetBox?.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      return;
    }

    const formatNumber = (num: number): string => {
      if (num >= 1_000_000_000_000) return (num / 1_000_000_000_000).toFixed(2) + 'T';
      if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + 'B';
      if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
      if (num >= 1_000) return (num / 1_000).toFixed(2) + 'K';
      return num.toString();
    };

    let html = `
      <div class="eventsMain breakDownCountry">
        <div class="item header">
          <p data-testing-id="header-country">Country</p>
          <p data-testing-id="header-population">Population %</p>
          <p data-testing-id="header-crypto-owners">Crypto Owners</p>
          <p data-testing-id="header-total-worth">Total Worth</p>
        </div>
    `;

    for (let idx = 0; idx < countryData.length; idx++) {
      const item = countryData[idx];
      const totalWorth = formatNumber(item.totalWorth);

      html += `
        <div class="item body" data-testing-id="country-item-${idx}" data-country="${item.country.replace(/[^a-zA-Z0-9]/g, '_')}">
          <p data-testing-id="country-item-${idx}-country"><span class="flagIcon" data-testing-id="country-item-${idx}-flag">${item.flag}</span>${item.country}</p>
          <p data-testing-id="country-item-${idx}-population">${item.populationPercentage}%</p>
          <p data-testing-id="country-item-${idx}-crypto-owners">${formatNumber(item.cryptoOwners)}</p>
          <p class="stat" data-testing-id="country-item-${idx}-total-worth">$${totalWorth}</p>
        </div>
      `;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#eventsContainer') as HTMLElement;

  const hasData =
    this.currentData?.data &&
    Array.isArray(this.currentData.data) &&
    this.currentData.data.length > 0;

  if (hasData) {
    container?.setAttribute('data-testing-id', 'crypto-breakdown-country');
  } else {
    container?.removeAttribute('data-testing-id');
  }

    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData.data)) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }
    this.displayCountryData();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.config.symbol = symbol;
    this.setupChart();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }
}

declare global { interface Window { TraderverseCryptoBreakDownByCountry: typeof TraderverseCryptoBreakDownByCountry; } }
if (typeof window !== 'undefined') { window.TraderverseCryptoBreakDownByCountry = TraderverseCryptoBreakDownByCountry; }